import { isEmpty } from "lodash";

import { getUpgradeHvacRowLabels } from "utils/upgrades";

/**
 * H2K `upgradeType` values (library / package `upgrade.type`) that map to **multiple**
 * ECM / community table row ids (`upgradeSubType`). One saved package upgrade can carry
 * several of these rows (e.g. one `heatingCooling` object with space heating + cooling fields).
 *
 * Order is stable: used when emitting `addUpgradeToCommunity` / parametric ECM slices.
 */
export const COMBINED_H2K_ECM_SUB_TYPES = {
    heatingCooling: ["spaceHeating", "spaceCooling", "secondaryHeating"],
    domesticHotWater: ["primaryHotWater", "dwhr"],
    basement: ["basementWall", "basementSlab"],
    ceiling: ["ceilingWithAttic", "cathedral"],
    window: ["window", "skylights"],
};

const isUsableEcmLabelString = (v) =>
    v != null && typeof v === "string" && v.trim() !== "" && v !== "None";

const firstMeaningfulLabelFromRecord = (labelObj) => {
    if (!labelObj || typeof labelObj !== "object") return "";
    for (const val of Object.values(labelObj)) {
        if (isUsableEcmLabelString(val)) return val;
    }
    return "";
};

const costSliceMeaningful = (cost, sub) => {
    const c = cost?.[sub];
    if (!c || typeof c !== "object") return false;
    if (typeof c.total === "number" && !Number.isNaN(c.total) && c.total > 0) return true;
    const items = c.items;
    if (items && typeof items === "object" && Object.keys(items).length > 0) return true;
    return false;
};

/**
 * Flat `ecmInputMap` row label for a section, preferring `upgrade.label[sectionType]` (export / tables).
 */
export const resolveEcmSectionLabelFromUpgradePayload = (sectionType, upgrade) => {
    const lbl = upgrade?.label;
    if (lbl == null) return "";
    if (typeof lbl === "string") return lbl;
    if (typeof lbl !== "object") return "";
    const direct = lbl[sectionType];
    if (direct != null && direct !== "" && direct !== "None") return direct;
    for (const v of Object.values(lbl)) {
        if (v != null && v !== "" && v !== "None") return v;
    }
    return "";
};

/**
 * Resolve the display label for one ECM row of a **composite** H2K upgrade (e.g. `spaceCooling` row
 * when only `label.spaceHeating` was persisted on the package).
 */
const heatingCoolingLabelFromFields = (upgradeSubType, fields) => {
    if (!fields || typeof fields !== "object" || isEmpty(fields)) return "";
    const packageId = "__synthetic__";
    const upgradeKey = "__synthetic__";
    const { label = "" } = getUpgradeHvacRowLabels({
        heatingCooling: {},
        ventilation: {},
        rowId: upgradeSubType,
        upgradePackage: {
            [packageId]: {
                upgrades: {
                    [upgradeKey]: {
                        upgradeType: "heatingCooling",
                        upgradeSubType,
                        fields,
                    },
                },
            },
        },
    });
    return typeof label === "string" ? label : "";
};

/**
 * @param {object} [fields] — `heatingCooling` package `fields`; used to derive a row label when
 *   persisted `label` keys disagree (e.g. spaceCooling stored as "None" while cooling HP fields exist).
 */
export const resolveCompositeEcmRowLabel = (labelRecord, upgradeSubType, h2kType, fields = null) => {
    if (isUsableEcmLabelString(labelRecord?.[upgradeSubType])) {
        return labelRecord[upgradeSubType];
    }

    const combined = COMBINED_H2K_ECM_SUB_TYPES[h2kType];
    if (!combined?.includes(upgradeSubType)) {
        const fallback = firstMeaningfulLabelFromRecord(labelRecord);
        return isUsableEcmLabelString(fallback) ? fallback : "None";
    }

    const peerOrder = combined.filter((id) => id !== upgradeSubType);
    if (!peerOrder.includes(h2kType)) {
        peerOrder.push(h2kType);
    }
    for (const key of peerOrder) {
        const v = labelRecord?.[key];
        if (isUsableEcmLabelString(v)) return v;
    }

    const fallback = firstMeaningfulLabelFromRecord(labelRecord);
    if (isUsableEcmLabelString(fallback)) return fallback;

    if (h2kType === "heatingCooling" && fields) {
        const derived = heatingCoolingLabelFromFields(upgradeSubType, fields);
        if (isUsableEcmLabelString(derived)) return derived;
    }

    return "None";
};

/** Merged `packageValues.label` for composite upgrades so sibling keys are not dropped on save. */
export const buildCompositePackageLabelMap = (labelRecord, upgradeSubType, rowLabel, h2kType) => {
    const combined = COMBINED_H2K_ECM_SUB_TYPES[h2kType];
    if (!combined?.includes(upgradeSubType)) {
        return { [upgradeSubType]: rowLabel };
    }
    const base =
        labelRecord && typeof labelRecord === "object" && !Array.isArray(labelRecord) ? { ...labelRecord } : {};
    base[upgradeSubType] = rowLabel;
    return base;
};

const COST_KEYS_IGNORE = new Set(["total", "items"]);

const addCostDrivenSubTypes = (cost, allowed, into) => {
    if (!cost || typeof cost !== "object") return;

    Object.keys(cost).forEach((k) => {
        if (COST_KEYS_IGNORE.has(k)) return;
        if (!allowed.includes(k)) return;
        if (cost[k] == null || typeof cost[k] !== "object") return;
        into.add(k);
    });
};

const addLabelSubTypes = (label, allowed, into) => {
    if (!label || typeof label !== "object") return;

    Object.keys(label).forEach((k) => {
        if (!allowed || allowed.includes(k)) into.add(k);
    });
};

const addHeatingCoolingFieldHints = (fields, into) => {
    if (!fields || typeof fields !== "object") return;

    if (!isEmpty(fields.heating)) into.add("spaceHeating");
    if (!isEmpty(fields.cooling)) into.add("spaceCooling");
    if (!isEmpty(fields.supplSystems)) into.add("secondaryHeating");
};

/** True when `primarySystem` clearly defines a tank / fuel / HPWH (not DWHR-only fragments). */
const primaryDhwTankFieldsPresent = (primarySystem) => {
    if (!primarySystem || typeof primarySystem !== "object" || isEmpty(primarySystem)) return false;
    return (
        primarySystem.tankType != null ||
        primarySystem.tankVolume != null ||
        primarySystem.energySource != null ||
        primarySystem.energyFactor != null ||
        primarySystem.heatPumpCOP != null ||
        primarySystem.tankLocation != null
    );
};

const addDomesticHotWaterFieldHints = (fields, into) => {
    if (!fields || typeof fields !== "object") return;
    const ps = fields.primarySystem;
    if (primaryDhwTankFieldsPresent(ps)) into.add("primaryHotWater");
    if (ps && (ps.hasDrainWaterHeatRecovery || !isEmpty(ps.drainWaterHeatRecovery))) into.add("dwhr");
};

/**
 * Whether a composite ECM subtype should receive a shared auto-generated label when it is still
 * empty (drawer save). Mirrors {@link collectEcmSubTypesForPackageUpgrade} where field hints exist.
 */
export const compositeSubtypeShouldInheritLabel = (h2kType, subType, fields = {}, cost = {}) => {
    const combined = COMBINED_H2K_ECM_SUB_TYPES[h2kType];
    if (!combined?.includes(subType)) return false;

    if (h2kType === "heatingCooling") {
        if (subType === "spaceHeating") return !isEmpty(fields.heating);
        if (subType === "spaceCooling") return !isEmpty(fields.cooling);
        if (subType === "secondaryHeating") return !isEmpty(fields.supplSystems);
        return false;
    }

    if (h2kType === "domesticHotWater") {
        const ps = fields.primarySystem;
        if (subType === "primaryHotWater") return primaryDhwTankFieldsPresent(ps);
        if (subType === "dwhr") {
            return !!(ps && (ps.hasDrainWaterHeatRecovery || !isEmpty(ps.drainWaterHeatRecovery)));
        }
        return false;
    }

    if (h2kType === "basement") {
        if (subType === "basementWall") {
            return costSliceMeaningful(cost, "basementWall") || !isEmpty(fields.wall);
        }
        if (subType === "basementSlab") {
            return costSliceMeaningful(cost, "basementSlab") || !isEmpty(fields.floor);
        }
        return false;
    }

    if (h2kType === "ceiling") {
        if (costSliceMeaningful(cost, subType)) return true;
        const id = fields?.constructionType?.id;
        if (subType === "ceilingWithAttic") return id === 0;
        if (subType === "cathedral") return id === 2;
        return false;
    }

    if (h2kType === "window") {
        return costSliceMeaningful(cost, subType);
    }

    return false;
};

/**
 * For a single upgrade object from an upgrade **package** (library load), returns which
 * `ecmInputMap` section keys (`upgradeSubType`) should receive a row.
 *
 * - **Combined** H2K types: unions label keys, matching `cost` keys, and (for HVAC) non-empty
 *   `fields` branches, then restricts to {@link COMBINED_H2K_ECM_SUB_TYPES}.
 * - **Other** types: preserves previous behaviour — label keys if any, else `upgradeSubType`.
 */
export const collectEcmSubTypesForPackageUpgrade = (upgrade) => {
    if (!upgrade || typeof upgrade !== "object") return [];

    const h2kType = upgrade.upgradeType;
    const combined = COMBINED_H2K_ECM_SUB_TYPES[h2kType];

    if (!combined) {
        const fromLabel = upgrade.label && typeof upgrade.label === "object" ? Object.keys(upgrade.label) : [];
        if (fromLabel.length >= 1) return fromLabel;
        if (upgrade.upgradeSubType) return [upgrade.upgradeSubType];
        return [];
    }

    const candidates = new Set();

    addLabelSubTypes(upgrade.label, combined, candidates);
    addCostDrivenSubTypes(upgrade.cost, combined, candidates);

    if (h2kType === "heatingCooling") {
        addHeatingCoolingFieldHints(upgrade.fields, candidates);
    }

    if (h2kType === "domesticHotWater") {
        addDomesticHotWaterFieldHints(upgrade.fields, candidates);
    }

    if (candidates.size === 0 && upgrade.upgradeSubType && combined.includes(upgrade.upgradeSubType)) {
        candidates.add(upgrade.upgradeSubType);
    }

    return combined.filter((id) => candidates.has(id));
};
